import { joinClassNames } from '@vertex-protocol/web-common';
import { Icons, Spinner } from '@vertex-protocol/web-ui';
import { ChainIcon } from './NadoChainSelect';
import { TokenIcon } from './NadoTokenSelect';
import { NadoBridgeProgressData } from '../types';
import { useEffect, useState } from 'react';

interface Props {
  data: NadoBridgeProgressData;
  /** Amount being bridged (display string) */
  amount: string;
  /** Token symbol on destination (may differ for cross-token) */
  destinationTokenSymbol?: string;
  /** Called when modal should close (bridge delivered or user dismisses) */
  onClose?: () => void;
}

/**
 * Rabby-style bridge status modal.
 *
 * Shows source chain → destination chain with token amounts,
 * status badges, elapsed time, and a link to the block explorer.
 * Auto-closes when the bridge delivers.
 */
export function NadoBridgeStatusModal({
  data,
  amount,
  destinationTokenSymbol,
  onClose,
}: Props) {
  const { status, sourceTxHash, sourceChain, tokenSymbol } = data;
  const destTokenSymbol = destinationTokenSymbol ?? tokenSymbol;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Track elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-close on delivery
  useEffect(() => {
    if (status === 'delivered' && onClose) {
      const timeout = setTimeout(onClose, 1500); // Brief pause to show success
      return () => clearTimeout(timeout);
    }
  }, [status, onClose]);

  const explorerUrl = sourceTxHash
    ? `${sourceChain.blockExplorerUrl}/tx/${sourceTxHash}`
    : undefined;

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div
      className={joinClassNames(
        'fixed inset-0 flex items-center justify-center z-[60]',
        'bg-black/60 backdrop-blur-sm',
      )}
      onClick={() => onClose?.()}
    >
        <div
          className={joinClassNames(
            'bg-surface-card border-stroke border rounded-xl',
            'w-96 max-w-[90vw] shadow-elevation-dialog',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-stroke">
            <div className="flex items-center gap-x-2">
              <Icons.ArrowDown className="text-text-secondary size-5" />
              <span className="text-text-primary text-base font-medium">
                Bridge Status
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-secondary transition-colors"
            >
              <Icons.X size={18} />
            </button>
          </div>

          {/* Source chain */}
          <div className="px-5 py-4">
            <div className="bg-surface-2 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-x-2">
                  <span className="text-text-secondary text-sm font-medium">1</span>
                  <span className="text-text-primary text-sm font-medium">
                    Sending from {sourceChain.name}
                  </span>
                </div>
                <StatusBadge status={status === 'failed' ? 'failed' : 'completed'} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                  <TokenIcon symbol={tokenSymbol} />
                  <span className="text-text-primary text-sm">
                    - {amount} {tokenSymbol}
                  </span>
                </div>
                <span className="text-text-tertiary text-xs">
                  -${amount}
                </span>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center py-2">
              <Icons.ArrowDown className="text-text-tertiary size-4" />
            </div>

            {/* Destination chain */}
            <div className="bg-surface-2 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-x-2">
                  <span className="text-text-secondary text-sm font-medium">2</span>
                  <span className="text-text-primary text-sm font-medium">
                    Receiving on Ink
                  </span>
                </div>
                <StatusBadge status={status} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                  <TokenIcon symbol={destTokenSymbol} />
                  <span className="text-text-primary text-sm">
                    + {amount} {destTokenSymbol}
                  </span>
                </div>
                <span className="text-text-tertiary text-xs">
                  +${amount}
                </span>
              </div>
              {status === 'in_transit' && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-stroke">
                  <span className="text-text-tertiary text-xs">Estimated time</span>
                  <span className="text-text-secondary text-xs">~1–3 min</span>
                </div>
              )}
            </div>

            {/* Footer: elapsed time + explorer link */}
            <div className="flex items-center justify-between mt-4">
              <span className="text-text-tertiary text-xs">
                Elapsed: {formatTime(elapsedSeconds)}
              </span>
              {explorerUrl && (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-tertiary hover:text-text-secondary flex items-center gap-x-1 text-xs transition-colors"
                >
                  View on explorer
                  <Icons.Link size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: 'in_transit' | 'delivered' | 'partial_arrival' | 'arrival_timeout' | 'failed' | 'completed';
}) {
  if (status === 'completed' || status === 'delivered') {
    return (
      <span className="bg-positive/20 text-positive flex items-center gap-x-1 rounded-full px-2 py-0.5 text-xs font-medium">
        <Icons.CheckCircle size={12} />
        Completed
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="bg-negative/20 text-negative flex items-center gap-x-1 rounded-full px-2 py-0.5 text-xs font-medium">
        <Icons.XCircle size={12} />
        Failed
      </span>
    );
  }
  return (
    <span className="bg-warning/20 text-warning flex items-center gap-x-1 rounded-full px-2 py-0.5 text-xs font-medium">
      <Spinner size={12} />
      Pending
    </span>
  );
}
